import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { InfoIcon } from '../Icon';
import { TextLink } from '../TextLink';
import Tooltip from '../Tooltip';
import { ValidationAnnouncement } from '../ValidationAnnouncement';
import { useValidationAnnouncement } from '../../hooks/useValidationAnnouncement';

import './FormList.css';

export interface FormListItem<T = Record<string, unknown>> {
  id: string;
  data: T;
}

export interface FormListFieldError {
  [fieldName: string]: string | undefined;
}

export interface FormListProps<T = Record<string, unknown>> {
  /**
   * Label for the entire form list section
   */
  label: string;
  /**
   * Optional tooltip text to display next to the label
   */
  tooltipText?: string;
  /**
   * Text for the "Add another" link
   */
  addLinkText: string;
  /**
   * List of saved items
   */
  items: FormListItem<T>[];
  /**
   * Current draft item being edited (the form fields)
   */
  draftItem: T;
  /**
   * Callback when items change (add, edit, remove)
   */
  onItemsChange: (items: FormListItem<T>[]) => void;
  /**
   * Callback when draft item changes
   */
  onDraftChange: (draft: T, isDraftActive?: boolean) => void;
  /**
   * Whether the user explicitly opened a new draft after saving an item.
   * When omitted, FormList manages this display state internally.
   */
  isDraftActive?: boolean;
  /**
   * Called when the user opens a new draft. Closing a draft is reported through
   * onDraftChange so clearing the draft and its active state can be atomic.
   */
  onDraftActiveChange?: (isActive: boolean) => void;
  /**
   * Validation function - returns error object with field names as keys
   * Return empty object or undefined if valid
   */
  validateItem: (item: T) => FormListFieldError;
  /**
   * Check if draft item has any data entered
   */
  isDraftEmpty: (draft: T) => boolean;
  /**
   * Generate a default/empty item
   */
  getEmptyItem: () => T;
  /**
   * Render the editable form fields for an item
   */
  renderEditForm: (item: T, onChange: (item: T) => void, errors: FormListFieldError, isEditing: boolean) => ReactNode;
  /**
   * Render the read-only display of a saved item (used when editing)
   */
  renderReadOnlyItem: (item: T, index: number) => ReactNode;
  /**
   * Render a compact summary card for a saved item (optional)
   * If provided, this is used for the default display; renderReadOnlyItem is used when editing
   * Details can be a string or an array of strings for multiple lines
   */
  renderItemSummary?: (item: T, index: number) => { title: string; details: string | string[] };
  /**
   * Additional class name for the container
   */
  className?: string;
  /**
   * Hide the visual list label/header when the surrounding form already
   * provides the needed context.
   */
  hideLabel?: boolean;
  /**
   * Field errors from parent (typically for the first item during workflow validation)
   */
  fieldErrors?: FormListFieldError;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

/**
 * A generic form list component that allows adding multiple items.
 * Each item must be fully validated before adding another.
 * Previously saved items are shown as read-only with Edit/Remove actions.
 */
export function FormList<T = Record<string, unknown>>({
  label,
  tooltipText,
  addLinkText,
  items,
  draftItem,
  onItemsChange,
  onDraftChange,
  isDraftActive,
  onDraftActiveChange,
  validateItem,
  isDraftEmpty,
  getEmptyItem,
  renderEditForm,
  renderReadOnlyItem,
  renderItemSummary,
  className = '',
  hideLabel = false,
  fieldErrors: externalFieldErrors,
  'data-testid': dataTestId,
}: FormListProps<T>) {
  const [internalErrors, setInternalErrors] = useState<FormListFieldError>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [editingErrors, setEditingErrors] = useState<FormListFieldError>({});
  const [draftFormKey, setDraftFormKey] = useState(0);
  const [internalDraftActive, setInternalDraftActive] = useState(() => items.length === 0 || !isDraftEmpty(draftItem));
  const [statusMessage, setStatusMessage] = useState('');
  const baseTestId = dataTestId ?? 'form-list';

  /**
   * ACP-2092 (Section 508): Add / Save validate the entry and stop when it is
   * incomplete. The errors rendered on the nested fields, but nothing announced them
   * and focus stayed on the link, so a screen reader user pressed "Add Another" and
   * heard nothing at all — the list simply did not grow.
   *
   * Scoped to this list: the surrounding sub-step has its own validated fields, and the
   * workflow announces those separately when Next is pressed. An unscoped lookup here
   * would announce both sets at once and send focus outside the entry being edited.
   */
  /**
   * ACP-2092 (Section 508): adding or removing an entry changed the list without a word
   * — JAWS just repeated the link that was pressed. A polite status reports it, in the
   * same shape as the error announcement. Screen reader only: nothing changes on screen.
   * The entry is named after the Add link ("Add Another Criminal Correction").
   */
  const itemName = addLinkText.replace(/^add another\s+/i, '').trim() || 'Entry';
  const reportListChange = useCallback(
    (verb: 'added' | 'removed', count: number) => {
      const saved = count === 1 ? '1 entry saved' : `${count} entries saved`;
      setStatusMessage(`${itemName} ${verb}. ${saved}.`);
    },
    [itemName],
  );

  // Check if external errors has any actual error values (not just undefined)
  const hasExternalErrors =
    externalFieldErrors && Object.values(externalFieldErrors).some((error) => error !== undefined);

  const draftErrors = hasExternalErrors ? externalFieldErrors : internalErrors;
  const draftIsActive = isDraftActive ?? internalDraftActive;
  const showDraft = editingIndex === null && (items.length === 0 || draftIsActive || !isDraftEmpty(draftItem));

  // Once the errors are gone (corrected, or the entry was added), the announcement has to
  // go too: the live region is a standing element, and a screen reader browsing past it
  // would otherwise read "1 field needs your attention" about a field that is fine.
  const errorSignature = [draftErrors, editingErrors]
    .flatMap((errors) => Object.entries(errors ?? {}))
    .filter(([, message]) => Boolean(message))
    .map(([field]) => field)
    .join('|');

  const {
    announcement: errorAnnouncement,
    reportInvalidFields,
    scopeRef,
  } = useValidationAnnouncement<HTMLDivElement>({ errorSignature });

  /**
   * ACP-2092 (Section 508): Remove unmounts the link that was pressed, and the browser
   * drops focus to the document — JAWS read "Access CMS" and the keyboard user was sent
   * back to the top of the page. Land on the entry that took its place instead, or the
   * one before it, or the Add link when the list is now empty.
   */
  const focusAfterRemoveRef = useRef<number | null>(null);

  useEffect(() => {
    const removedIndex = focusAfterRemoveRef.current;
    if (removedIndex === null) return;
    focusAfterRemoveRef.current = null;

    const root = scopeRef.current;
    if (!root) return;

    const target =
      root.querySelector<HTMLElement>(`a[data-testid="${baseTestId}-item-edit-${removedIndex}"]`) ??
      root.querySelector<HTMLElement>(`a[data-testid="${baseTestId}-item-edit-${removedIndex - 1}"]`) ??
      root.querySelector<HTMLElement>(`a[data-testid="${baseTestId}-add-link"]`) ??
      root.querySelector<HTMLElement>('.form-list__draft input, .form-list__draft select, .form-list__draft textarea');

    target?.focus();
  }, [items, baseTestId, scopeRef]);

  const generateId = useCallback(() => {
    return `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const handleAddClick = useCallback(() => {
    // If currently editing an existing item, don't allow adding
    if (editingIndex !== null) {
      return;
    }

    // Validate the draft item
    const errors = validateItem(draftItem);
    const hasErrors = Object.values(errors).some((error) => error !== undefined);

    if (hasErrors) {
      setInternalErrors(errors);
      // Batched with setInternalErrors, so the effect sees the fields already invalid.
      reportInvalidFields();

      return;
    }

    // Check if draft is empty
    if (isDraftEmpty(draftItem)) {
      return;
    }

    // Add the item to the list
    const newItem: FormListItem<T> = {
      data: { ...draftItem },
      id: generateId(),
    };

    onItemsChange([...items, newItem]);
    onDraftChange(getEmptyItem(), false);
    setInternalDraftActive(false);
    setInternalErrors({});
    setDraftFormKey((prev) => prev + 1);
    reportListChange('added', items.length + 1);
  }, [
    draftItem,
    items,
    validateItem,
    isDraftEmpty,
    onItemsChange,
    onDraftChange,
    getEmptyItem,
    generateId,
    editingIndex,
    reportInvalidFields,
    reportListChange,
  ]);

  const handleRemove = useCallback(
    (index: number) => {
      const newItems = items.filter((_, i) => i !== index);
      focusAfterRemoveRef.current = index;
      onItemsChange(newItems);
      reportListChange('removed', newItems.length);

      // If we were editing this item, cancel the edit
      if (editingIndex === index) {
        setEditingIndex(null);
        setEditingItem(null);
        setEditingErrors({});
      } else if (editingIndex !== null && editingIndex > index) {
        // Adjust editing index if we removed an item before it
        setEditingIndex(editingIndex - 1);
      }
    },
    [items, onItemsChange, editingIndex, reportListChange],
  );

  const handleEditStart = useCallback(
    (index: number) => {
      // If there's a non-empty draft, validate it first
      if (!isDraftEmpty(draftItem)) {
        const errors = validateItem(draftItem);
        const hasErrors = Object.values(errors).some((error) => error !== undefined);

        if (hasErrors) {
          setInternalErrors(errors);
          reportInvalidFields();

          return;
        }

        // Save the draft first
        const newItem: FormListItem<T> = {
          data: { ...draftItem },
          id: generateId(),
        };
        onItemsChange([...items, newItem]);
        onDraftChange(getEmptyItem(), false);
        setInternalDraftActive(false);
        setInternalErrors({});
        reportListChange('added', items.length + 1);
      }

      setEditingIndex(index);
      setEditingItem({ ...items[index].data });
      setEditingErrors({});
    },
    [
      items,
      draftItem,
      validateItem,
      isDraftEmpty,
      onItemsChange,
      onDraftChange,
      getEmptyItem,
      generateId,
      reportInvalidFields,
      reportListChange,
    ],
  );

  const handleEditSave = useCallback(() => {
    if (editingIndex === null || editingItem === null) return;

    // Validate the editing item
    const errors = validateItem(editingItem);
    const hasErrors = Object.values(errors).some((error) => error !== undefined);

    if (hasErrors) {
      setEditingErrors(errors);
      reportInvalidFields();

      return;
    }

    // Update the item in the list
    const newItems = [...items];
    newItems[editingIndex] = {
      ...newItems[editingIndex],
      data: { ...editingItem },
    };

    onItemsChange(newItems);
    setEditingIndex(null);
    setEditingItem(null);
    setEditingErrors({});
  }, [editingIndex, editingItem, items, validateItem, onItemsChange, reportInvalidFields]);

  const handleEditCancel = useCallback(() => {
    setEditingIndex(null);
    setEditingItem(null);
    setEditingErrors({});
  }, []);

  const handleDraftChange = useCallback(
    (newDraft: T) => {
      onDraftChange(newDraft, true);
      setInternalDraftActive(true);
      // Clear errors when user makes changes
      if (Object.keys(internalErrors).length > 0) {
        setInternalErrors({});
      }
    },
    [onDraftChange, internalErrors],
  );

  const handleEditingItemChange = useCallback((newItem: T) => {
    setEditingItem(newItem);
    // Clear errors when user makes changes
    setEditingErrors({});
  }, []);

  const handleCancelDraft = useCallback(() => {
    onDraftChange(getEmptyItem(), false);
    setInternalDraftActive(false);
    setInternalErrors({});
    setDraftFormKey((prev) => prev + 1);
  }, [onDraftChange, getEmptyItem]);

  const handleStartDraft = useCallback(() => {
    setInternalDraftActive(true);
    setInternalErrors({});
    onDraftActiveChange?.(true);
  }, [onDraftActiveChange]);

  return (
    <div
      ref={scopeRef}
      className={`form-list ${className}`}
      data-testid={baseTestId}>
      <span
        aria-live='polite'
        className='ds-u-visibility--screen-reader'
        data-testid={`${baseTestId}-status`}
        role='status'>
        {statusMessage}
      </span>
      <ValidationAnnouncement
        data-testid={`${baseTestId}-error-announcement`}
        message={errorAnnouncement}
      />
      {!hideLabel && (
        <div
          className='form-list__header'
          data-testid={`${baseTestId}-header`}>
          <span
            className='form-list__label-content'
            data-testid={`${baseTestId}-label`}>
            {label}
            {tooltipText && (
              <span className='form-list__label-suffix'>
                <Tooltip
                  className='form-list__info-tooltip'
                  data-testid={`${baseTestId}-tooltip`}
                  id={`${label.replace(/\s+/g, '-').toLowerCase()}-tooltip`}
                  placement='top'
                  title={tooltipText}>
                  <InfoIcon />
                </Tooltip>
              </span>
            )}
          </span>
        </div>
      )}

      {items.map((item, index) => (
        <div
          key={item.id}
          className='form-list__item'
          data-testid={`${baseTestId}-item-${index}`}>
          {editingIndex === index && editingItem !== null ? (
            <div
              className='form-list__item-edit'
              data-testid={`${baseTestId}-item-edit-${index}`}>
              <div
                className='form-list__item-form'
                data-testid={`${baseTestId}-item-edit-form-${index}`}>
                {renderEditForm(editingItem, handleEditingItemChange, editingErrors, true)}
              </div>
              <div
                className='form-list__item-actions form-list__item-actions--editing'
                data-testid={`${baseTestId}-item-edit-actions-${index}`}>
                <TextLink
                  underline
                  ariaLabel={`Save changes to item ${index + 1}`}
                  data-testid={`${baseTestId}-item-edit-save-${index}`}
                  text='Save'
                  onClick={handleEditSave}
                />
                <span className='form-list__action-separator'>|</span>
                <TextLink
                  underline
                  ariaLabel={`Cancel editing item ${index + 1}`}
                  data-testid={`${baseTestId}-item-edit-cancel-${index}`}
                  text='Cancel'
                  onClick={handleEditCancel}
                />
              </div>
            </div>
          ) : renderItemSummary ? (
            (() => {
              const summary = renderItemSummary(item.data, index);
              const detailLines = Array.isArray(summary.details) ? summary.details : [summary.details];

              return (
                <div
                  className='form-list__saved-item-card'
                  data-testid={`${baseTestId}-item-summary-${index}`}>
                  <div
                    className='form-list__saved-item-summary'
                    data-testid={`${baseTestId}-item-summary-content-${index}`}>
                    <span
                      className='form-list__saved-item-title'
                      data-testid={`${baseTestId}-item-summary-title-${index}`}>
                      {summary.title}
                    </span>
                    {detailLines.map((line, lineIndex) => (
                      <span
                        key={lineIndex}
                        className='form-list__saved-item-details'
                        data-testid={`${baseTestId}-item-summary-detail-${index}-${lineIndex}`}>
                        {line}
                      </span>
                    ))}
                  </div>
                  <div
                    className='form-list__saved-item-actions'
                    data-testid={`${baseTestId}-item-summary-actions-${index}`}>
                    <TextLink
                      underline
                      ariaLabel={`Edit item ${index + 1}`}
                      data-testid={`${baseTestId}-item-edit-${index}`}
                      text='Edit'
                      onClick={() => handleEditStart(index)}
                    />
                    <span className='form-list__action-separator'>|</span>
                    <TextLink
                      underline
                      ariaLabel={`Remove item ${index + 1}`}
                      data-testid={`${baseTestId}-item-remove-${index}`}
                      text='Remove'
                      onClick={() => handleRemove(index)}
                    />
                  </div>
                </div>
              );
            })()
          ) : (
            <div
              className='form-list__item-readonly'
              data-testid={`${baseTestId}-item-readonly-${index}`}>
              <div
                className='form-list__item-content'
                data-testid={`${baseTestId}-item-readonly-content-${index}`}>
                {renderReadOnlyItem(item.data, index)}
              </div>
              <div
                className='form-list__item-actions'
                data-testid={`${baseTestId}-item-readonly-actions-${index}`}>
                <TextLink
                  underline
                  ariaLabel={`Edit item ${index + 1}`}
                  data-testid={`${baseTestId}-item-edit-${index}`}
                  text='Edit'
                  onClick={() => handleEditStart(index)}
                />
                <span className='form-list__action-separator'>|</span>
                <TextLink
                  underline
                  ariaLabel={`Remove item ${index + 1}`}
                  data-testid={`${baseTestId}-item-remove-${index}`}
                  text='Remove'
                  onClick={() => handleRemove(index)}
                />
              </div>
            </div>
          )}
        </div>
      ))}

      {showDraft && (
        <div
          key={draftFormKey}
          className='form-list__draft'
          data-testid={`${baseTestId}-draft`}>
          <div
            className='form-list__draft-form'
            data-testid={`${baseTestId}-draft-form`}>
            {renderEditForm(draftItem, handleDraftChange, draftErrors, false)}
          </div>
        </div>
      )}

      {editingIndex === null && showDraft && (
        <div
          className='form-list__add-link'
          data-testid={`${baseTestId}-draft-actions`}>
          <TextLink
            underline
            ariaLabel={`Save ${itemName}`}
            data-testid={`${baseTestId}-draft-save`}
            text={`Save ${itemName}`}
            onClick={handleAddClick}
          />
          {items.length > 0 && (
            <>
              <span className='form-list__action-separator'>|</span>
              <TextLink
                underline
                ariaLabel={`Cancel adding ${itemName}`}
                data-testid={`${baseTestId}-draft-cancel`}
                text='Cancel'
                onClick={handleCancelDraft}
              />
            </>
          )}
        </div>
      )}

      {editingIndex === null && !showDraft && (
        <div
          className='form-list__add-link'
          data-testid={`${baseTestId}-add`}>
          <TextLink
            underline
            ariaLabel={addLinkText}
            data-testid={`${baseTestId}-add-link`}
            text={addLinkText}
            onClick={handleStartDraft}
          />
        </div>
      )}
    </div>
  );
}

export default FormList;
